"use client"

import api from "../axios/index"
import { handleApiError } from "../../utils/handleApiError"

export const fetchByUrl = async (url) => {
  try {
    if (!url) return null;

    let path = url;
    try {
      const u = new URL(String(url));
      path = u.pathname + u.search;
    } catch (e) {
      path = url;
    }

    const response = await api.get(path, { withCredentials: true });
    return response.data ?? response;
  } catch (err) {
    handleApiError(err);
    return null;
  }
};

const getActiveClientId = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const storedAcc = localStorage.getItem("inventory_active_account");
    if (!storedAcc) return null;
    const account = JSON.parse(storedAcc);

    if (account?.role === "Admin") {
      return null;
    }
    return account?.id || null;
  } catch (err) {

    return null;
  }
}

api.interceptors.request.use((config) => {
  try {
    const clientId = getActiveClientId();
    if (!config.params) config.params = {};
    if (clientId !== null) config.params["current_client_id"] = clientId;
  } catch (e) {

  }
  return config;
});

export const getDeviceTypes = async () => {
  try {
    const response = await api.get(`/inventory/device-types/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export const getLocationList = async () => {
  try {
    const tenantId = getActiveClientId();
    const response = await api.get(`/accounts/${tenantId}/locations/?status=active`);
    const locs = response?.data?.locations || [];

    const sessionLoc = sessionStorage.getItem("current_loc");
    const user = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("user") || "null") : null;
    const user_current_loc = user?.current_location;

    if (typeof window !== "undefined") {
      if (user_current_loc) {
        const current_loc = locs.find((loc) => loc.id === Number(user_current_loc));
        localStorage.setItem("fallback_location", JSON.stringify(current_loc || locs[0] || {}));
        if (!sessionLoc) {
          sessionStorage.setItem("current_loc", JSON.stringify(current_loc || locs[0] || {}));
        }
      } else {
        localStorage.setItem("fallback_location", JSON.stringify(locs[0] || {}));
        if (!sessionLoc) {
          sessionStorage.setItem("current_loc", JSON.stringify(locs[0] || {}));
        }
      }
    }

    return { success: true, data: locs };
  } catch (error) {
    handleApiError(error);
    return { success: false, error: error.message };
  }
}

export const getBoxDeviceTypes = async () => {
  try {
    const response = await api.get(`/inventory/box-device-types/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export const getWarehouseStatuses = async () => {
  try {
    const response = await api.get(`/inventory/warehouse/statuses/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export const createWarehouseBox = async (payload) => {
  try {
    const response = await api.post(`/inventory/warehouse/boxes/create/`, payload);
    return response.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export const createWarehouseDevice = async (payload) => {
  try {
    const response = await api.post(`/inventory/warehouse/devices/create/`, payload);
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.errors) {
        return {
          success: false,
          errors: errorData.errors,
          message: "Device could not be created due to validation errors",
        }
      } else if (errorData.message || errorData.detail) {
        return {
          success: false,
          message: errorData.message || errorData.detail,
        }
      }
    }
    handleApiError(error)
    return { success: false, message: "Failed to create device" }
  }
}

export const bulkCreateWarehouseDevices = async (payload) => {
  try {
    const response = await api.post(`/inventory/warehouse/devices/bulk-create/`, payload);
    return { success: true, data: response.data }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.errors) {
      return {
        success: false,
        errors: error.response.data.errors,
        message: "Some devices could not be created due to validation errors",
      }
    }
    handleApiError(error)
    return { success: false, message: "Failed to create devices" }
  }
}

export const updateWarehouseDevice = async (deviceId, payload) => {
  try {
    const response = await api.patch(`/inventory/warehouse/devices/${deviceId}/`, payload);
    return { success: true, data: response.data }
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      if (errorData.errors) {
        return {
          success: false,
          errors: errorData.errors,
          message: "Device could not be updated due to validation errors",
        }
      } else if (errorData.message || errorData.detail) {
        return {
          success: false,
          message: errorData.message || errorData.detail,
        }
      }
    }
    handleApiError(error)
    return { success: false, message: "Failed to update device" }
  }
}

export const deleteWarehouseDevice = async (deviceId) => {
  try {
    await api.delete(`/inventory/warehouse/devices/${deviceId}/`);
    return { success: true }
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      return {
        success: false,
        message: errorData.message || errorData.detail || "Failed to delete device",
      }
    }
    handleApiError(error)
    return { success: false, message: "Failed to delete device" }
  }
}

export const deleteWarehouseBox = async (boxId) => {
  try {
    await api.delete(`/inventory/warehouse/boxes/${boxId}/`);
    return { success: true }
  } catch (error) {
    console.error("Error deleting warehouse box:", error)
    return { success: false, message: "Failed to delete box" }
  }
}

const fetchList = async (endpoint, params = {}) => {
  try {
    const response = await api.get(endpoint, { params });
    return response.data;
  } catch (error) {
    handleApiError(error);

    return { results: [], total_count: 0 };
  }
}

export const getBoxes = async (filters = {}) => {
  return fetchList(`/boxes/`, filters);
}

export const getWarehouseBoxes = async (filters = {}) => {
  return fetchList(`/inventory/warehouse/boxes/`, filters)
}

export const getWarehouseDevices = async (filters = {}) => {

  if ((typeof filters === 'number' || typeof filters === 'string') && filters !== '') {
    return fetchList(`/inventory/warehouse/devices/`, { box_id: filters });
  }

  return fetchList(`/inventory/warehouse/devices/`, filters);
}

export const getIndividualDevices = async (filters = {}) => {
  const params = { box_id: 'null', ...filters };
  return fetchList(`/inventory/warehouse/devices/`, params);
};

export const getClientDropshipOrders = async (filters = {}) => {
  return fetchList(`/inventory/toca/client-dropship-orders/`, filters)
}
export const getClientBulkOrders = async (filters = {}) => {
  try {
    const data = await fetchList(`/inventory/toca/client-bulk-orders/`, filters)

    return {
      success: true,
      data: {
        results: data.results || data.orders || [],
        meta: data.meta || {
          current_page: data.current_page || 1,
          total_pages: data.total_pages || 1,
          total_items: data.total_count || data.count || 0,
          next: data.next || null,
          previous: data.previous || null,
        },
      },
    }
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      return {
        success: false,
        message: errorData.message || errorData.detail || "Failed to fetch client orders",
      }
    }
    console.error("Error fetching client orders:", error)
    return {
      success: false,
      message: "Failed to fetch client orders",
      data: { results: [], meta: {} },
    }
  }
}

export const getTenoviBulkOrders = async (filters = {}) => {
  try {
    const data = await fetchList(`/inventory/toca/tenovi-bulk-orders/`, filters)
    return {
      success: true,
      data: {
        results: data.results || data.orders || [],
        meta: data.meta || {
          current_page: data.current_page || 1,
          total_pages: data.total_pages || 1,
          total_items: data.total_count || data.count || 0,
          next: data.next || null,
          previous: data.previous || null,
        },
      },
    }
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      return {
        success: false,
        message: errorData.message || errorData.detail || "Failed to fetch tenovi orders",
      }
    }
    console.error("Error fetching tenovi orders:", error)
    return {
      success: false,
      message: "Failed to fetch tenovi orders",
      data: { results: [], meta: {} },
    }
  }
}

export const getSupplies = async (type = "tenovi") => {
  try {
    const response = await api.get(`/inventory/toca/supplies/`, {
      params: { type },
    })
    return { success: true, data: response.data }
  } catch (error) {
    handleApiError(error)
    return { success: false, error: error.message }
  }
}

export const updateSupply = async (id, data) => {
  try {
    const response = await api.patch(`/inventory/toca/supplies/${id}/`, data)
    return { success: true, data: response.data }
  } catch (error) {
    console.error("Update supply error:", error)
    return { success: false, error: error.message }
  }
}

export const deleteSupply = async (id) => {
  try {
    const response = await api.delete(`/inventory/toca/supplies/${id}/`)
    return { success: true, data: response.data }
  } catch (error) {
    console.error("Delete supply error:", error)
    return { success: false, error: error.message }
  }
}

export const fetchSupplyTypes = async (type) => {
  try {
    const response = await api.get(`/inventory/toca/supply-types/`, {
      params: { type },
    })
    return { success: true, data: response.data }
  } catch (error) {
    handleApiError(error)
    return { success: false, error: error.message }
  }
}

export const createSupply = async (payload) => {
  try {
    const response = await api.post(`/inventory/toca/supplies/create/`, payload)
    return { success: true, data: response.data }
  } catch (error) {
    handleApiError(error)
    return { success: false, error: error.message }
  }
}

export const getTicketRequests = async (locationId = null, status = "not_forwarded") => {
  try {
    const params = {}

    if (status) params.status = status
    if (locationId) params.location_id = locationId

    const response = await api.get(`/inventory/toca/ticket-list/`, { params })
    return { success: true, data: response.data }
  } catch (error) {
    handleApiError(error)
    return { success: false, message: "Failed to fetch ticket requests" }
  }
}


export const updateTicketRequestStatus = async (ticketId, newStatus) => {
  try {
    const payload = { status: newStatus }

    const response = await api.patch(
      `/inventory/toca/ticket/${ticketId}/update-status/`,
      payload
    )

    return {
      success: true,
      message: response.data?.message || "Status updated successfully",
      data: response.data,
    }
  } catch (error) {
    handleApiError(error)
    return {
      success: false,
      message: "Failed to update ticket request status",
    }
  }
}

export const approveTicketRequest = async (ticketId) => {
  try {
    const response = await api.post(`/inventory/toca/ticket/${ticketId}/approve/`)

    return {
      success: true,
      message: response.data?.message || "Ticket approved & forwarded",
      data: response.data,
    }
  } catch (error) {
    handleApiError(error)
    return {
      success: false,
      message: "Failed to forward ticket",
    }
  }
}

export const getTicketEvents = async (tenoviTicketId) => {
  try {
    const response = await api.get(
      `/inventory/toca/tickets/${tenoviTicketId}/events/`
    )

    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    handleApiError(error)
    return {
      success: false,
      message: "Failed to fetch ticket events",
    }
  }
}
export const createTicketEvent = async (tenoviTicketId, payload) => {
  try {
    const response = await api.post(
      `/inventory/toca/tickets/${tenoviTicketId}/events/`,
      payload
    )

    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    handleApiError(error)
    return {
      success: false,
      message: "Failed to create ticket event",
    }
  }
}

export const getClientDevicesList = async (filters = {}) => {
  return fetchList(`/inventory/client/devices/`, filters);
}

export const updateWarehouseBox = async (boxId, payload) => {
  try {
    const response = await api.patch(`/inventory/warehouse/boxes/${boxId}/`, payload)
    return response.data
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      if (errorData.errors) {
        return {
          success: false,
          errors: errorData.errors,
          message: "Box could not be updated due to validation errors",
        }
      } else if (errorData.message || errorData.detail) {
        return {
          success: false,
          message: errorData.message || errorData.detail,
        }
      }
    }
    handleApiError(error)
    return { success: false, message: "Failed to update box" }
  }
}

export const createTenoviBulkOrder = async (payload) => {
  try {
    const response = await api.post(`/inventory/toca/tenovi-bulk-orders/create/`, payload)
    return { success: true, data: response.data }
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      return {
        success: false,
        message: errorData.message || errorData.detail || "Failed to create bulk order",
      }
    }
    console.error("Error creating bulk order:", error)
    return { success: false, message: "Failed to create bulk order" }
  }
}

export const approveDropshipOrder = async (orderId) => {
  try {
    const response = await api.post(`/inventory/toca/dropship-orders/${orderId}/approve/`)

    return {
      success: true,
      message: response.data.message || "Order approved successfully",
    }
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      return {
        success: false,
        message: errorData.message || errorData.detail || "Failed to approve dropship order",
      }
    }
    console.error("Error approving dropship order:", error)
    return {
      success: false,
      message: "Failed to approve dropship order",
    }
  }
}

export const updateDropshipOrderStatus = async (orderId, newStatus) => {
  try {
    const response = await api.patch(
      `/inventory/toca/client-dropship-order/${orderId}/update-status/`,
      { status: newStatus }
    )

    return {
      success: true,
      message: response.data.message || "Status updated successfully",
      status: response.data.status,
      orderId: response.data.order_id,
    }
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data
      return {
        success: false,
        message: errorData.message || errorData.detail || "Failed to update status",
      }
    }
    console.error("Error updating dropship order status:", error)
    return {
      success: false,
      message: "Failed to update status",
    }
  }
}

export const updateClientBulkOrderStatus = async (orderId, status) => {
  try {
    const response = await api.patch(
      `/inventory/toca/client-bulk-order/${orderId}/update-status/`,
      { status }
    )
    return { success: true, ...response.data }
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "Failed to update status",
    }
  }
}

export const updateTenoviBulkOrderStatus = async (orderId, status) => {
  try {
    const response = await api.patch(`/inventory/toca/tenovi-bulk-order/${orderId}/update-status/`, {
      status,
    })
    return response.data
  } catch (error) {
    console.error("Error updating tenovi bulk order status:", error)
    throw error
  }
}

export const getWarehouseBoxDetail = async (boxId) => {
  try {
    if (!boxId) return { success: false, message: "boxId required" };
    const response = await api.get(`/inventory/warehouse/boxes/${boxId}/`);
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      return { success: false, message: errorData.message || errorData.detail || "Failed to fetch box detail" };
    }
    handleApiError(error);
    return { success: false, message: "Failed to fetch box detail" };
  }
}